import { useEffect, useCallback, useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { locationApi, type LocationStatusResponse } from '@/api/location';

// Порог хорошей точности (метры)
const GOOD_ACCURACY = 15;

// Cooldown после хорошей позиции (5 минут)
const COOLDOWN_MS = 5 * 60 * 1000;

// Минимальный интервал между попытками сохранения (10 секунд)
const SAVE_THROTTLE_MS = 10_000;

export interface LocationAutoSaveState {
  isTracking: boolean;
  lastSaveAt: Date | null;
  nextSaveAt: Date | null;
  currentPosition: GeolocationPosition | null;
  error: GeolocationPositionError | null;
  permissionState: PermissionState | 'unknown';
  isSaving: boolean;
  serverStatus: LocationStatusResponse | null;
  inCooldown: boolean;
}

export function useLocationAutoSave() {
  const [state, setState] = useState<LocationAutoSaveState>({
    isTracking: false,
    lastSaveAt: null,
    nextSaveAt: null,
    currentPosition: null,
    error: null,
    permissionState: 'unknown',
    isSaving: false,
    serverStatus: null,
    inCooldown: false,
  });

  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Refs для управления watch и избежания stale closures
  const watchIdRef = useRef<number | null>(null);
  const lastGoodSaveRef = useRef<Date | null>(null);
  const lastSaveAttemptRef = useRef<number>(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const permissionDeniedRef = useRef(false);
  const isMutatingRef = useRef(false);

  // Server status query
  const { data: serverStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['location-status'],
    queryFn: locationApi.getStatus,
    staleTime: 60_000,
  });

  // Остановить watch
  const stopWatch = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setState((prev) => ({ ...prev, isTracking: false }));
    }
  }, []);

  // Функции для управления cooldown (вынесены чтобы избежать циклических зависимостей)
  const startCooldown = useCallback(() => {
    lastGoodSaveRef.current = new Date();
    setState((prev) => ({ ...prev, inCooldown: true }));

    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
    }

    cooldownTimerRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, inCooldown: false }));
      lastGoodSaveRef.current = null;
    }, COOLDOWN_MS);
  }, []);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: locationApi.saveLocation,
    onMutate: () => {
      isMutatingRef.current = true;
    },
    onSettled: () => {
      isMutatingRef.current = false;
    },
    onSuccess: (response) => {
      if (response.saved && response.location) {
        setState((prev) => ({
          ...prev,
          lastSaveAt: new Date(response.location!.recordedAt),
          nextSaveAt: null,
          isSaving: false,
        }));
        refetchStatus();

        // Если хорошая точность — запустить cooldown
        if (response.goodAccuracy) {
          stopWatch();
          startCooldown();
        }
      } else if (response.nextSaveAt) {
        setState((prev) => ({
          ...prev,
          nextSaveAt: new Date(response.nextSaveAt!),
          isSaving: false,
        }));
      } else {
        setState((prev) => ({ ...prev, isSaving: false }));
      }
    },
    onError: () => {
      setState((prev) => ({ ...prev, isSaving: false }));
    },
  });

  // Ref для mutate функции (чтобы избежать stale closure)
  const mutateRef = useRef(saveMutation.mutate);
  mutateRef.current = saveMutation.mutate;

  // Check permission state
  const checkPermission = useCallback(async (): Promise<PermissionState | 'unknown'> => {
    if (!isSupported) return 'unknown';
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setState((prev) => ({ ...prev, permissionState: result.state }));

        if (result.state === 'denied') {
          permissionDeniedRef.current = true;
        }

        result.onchange = () => {
          setState((prev) => ({ ...prev, permissionState: result.state }));
          permissionDeniedRef.current = result.state === 'denied';
        };

        return result.state;
      } catch {
        return 'unknown';
      }
    }
    return 'unknown';
  }, [isSupported]);

  // Запустить watch — стабильная функция без зависимости от mutation
  const startWatch = useCallback(() => {
    if (!isSupported || permissionDeniedRef.current) return;

    // Не запускать если уже работает
    if (watchIdRef.current !== null) return;

    // Проверить cooldown
    if (lastGoodSaveRef.current) {
      const elapsed = Date.now() - lastGoodSaveRef.current.getTime();
      if (elapsed < COOLDOWN_MS) {
        return;
      }
    }

    setState((prev) => ({ ...prev, isTracking: true, error: null }));

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const accuracy = position.coords.accuracy;
        const now = Date.now();

        setState((prev) => ({
          ...prev,
          currentPosition: position,
          error: null,
        }));

        // Throttle: не чаще чем раз в 10 секунд
        const timeSinceLastAttempt = now - lastSaveAttemptRef.current;
        const shouldSave = timeSinceLastAttempt >= SAVE_THROTTLE_MS && !isMutatingRef.current;

        if (shouldSave) {
          lastSaveAttemptRef.current = now;
          setState((prev) => ({ ...prev, isSaving: true }));
          mutateRef.current({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy,
          });
        }

        // Если хорошая точность — остановить watch
        if (accuracy <= GOOD_ACCURACY) {
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
            setState((prev) => ({ ...prev, isTracking: false }));
          }
          // Если не сохраняли — запустить cooldown сразу
          if (!shouldSave) {
            lastGoodSaveRef.current = new Date();
            setState((prev) => ({ ...prev, inCooldown: true }));
            if (cooldownTimerRef.current) {
              clearTimeout(cooldownTimerRef.current);
            }
            cooldownTimerRef.current = setTimeout(() => {
              setState((prev) => ({ ...prev, inCooldown: false }));
              lastGoodSaveRef.current = null;
            }, COOLDOWN_MS);
          }
        }
      },
      (error) => {
        if (error.code === error.TIMEOUT) {
          return;
        }

        if (error.code === error.PERMISSION_DENIED) {
          permissionDeniedRef.current = true;
          setState((prev) => ({
            ...prev,
            permissionState: 'denied',
            error,
            isTracking: false,
          }));
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
          return;
        }

        setState((prev) => ({ ...prev, error }));
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      }
    );
  }, [isSupported]);

  // Visibility change handler — запускается только при монтировании
  useEffect(() => {
    if (!isSupported) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!permissionDeniedRef.current) {
          startWatch();
        }
      } else {
        stopWatch();
      }
    };

    // Первоначальный запуск
    checkPermission().then((permState) => {
      if (permState !== 'denied') {
        startWatch();
      }
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopWatch();
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, [isSupported, startWatch, stopWatch, checkPermission]);

  // Update state with server status
  useEffect(() => {
    if (serverStatus) {
      setState((prev) => ({
        ...prev,
        serverStatus,
        lastSaveAt: serverStatus.lastLocation
          ? new Date(serverStatus.lastLocation.recordedAt)
          : prev.lastSaveAt,
        nextSaveAt: serverStatus.nextSaveAt
          ? new Date(serverStatus.nextSaveAt)
          : prev.nextSaveAt,
      }));
    }
  }, [serverStatus]);

  // Manual refresh
  const refresh = useCallback(() => {
    permissionDeniedRef.current = false;
    lastGoodSaveRef.current = null;
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    setState((prev) => ({ ...prev, inCooldown: false }));

    checkPermission().then(() => {
      stopWatch();
      startWatch();
    });
  }, [checkPermission, startWatch, stopWatch]);

  return {
    ...state,
    isSupported,
    refresh,
    refetchStatus,
  };
}
